import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { InputAdornment } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { NumberTextField } from '~/modules/common/components';
import { useMeContext } from '~/modules/me';
import { getReactNumberFormatFromMe } from '~/modules/common/numbers';
import { DISPLAY_UNIT_ENUM } from '~/modules/resourcing/common/enums';
import { ALLOCATION_PERIOD } from './AllocationPeriodTabs';

const useStyles = makeStyles(theme => ({
  inputField: {
    width: theme.spacing(24),
    '& .MuiOutlinedInput-root': {
      height: theme.spacing(4),
      borderRadius: theme.shape.borderRadius
    }
  },
  input: {
    ...theme.typography.body2,
    padding: theme.spacing(0, 1.5),
    '-moz-appearance': 'textfield',
    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    }
  },
  inputAdornment: {
    ...theme.typography.body2,
    color: theme.palette.text.secondary
  }
}));

const getUnitLabels = (selectedAllocationPeriod, displayUnit) => {
  switch (selectedAllocationPeriod) {
    case ALLOCATION_PERIOD.MONTH:
      return 'allocationByPeriod.inputUnit.hoursPerMonth';
    case ALLOCATION_PERIOD.WEEK:
      return 'allocationByPeriod.inputUnit.hoursPerWeek';
    case ALLOCATION_PERIOD.ENTIRE_PERIOD:
    default:
      return displayUnit === DISPLAY_UNIT_ENUM.PERCENTAGE
        ? 'allocationByPeriod.inputUnit.percent'
        : 'allocationByPeriod.inputUnit.hours';
  }
};

const AllocationInput = ({
  value,
  selectedAllocationPeriod,
  displayUnit,
  maxValue,
  onChange,
  ...rest
}) => {
  const classes = useStyles();
  const me = useMeContext();
  const { decimalScale } = getReactNumberFormatFromMe(me);

  const unitLabel = getUnitLabels(selectedAllocationPeriod, displayUnit);

  const endAdornment = useMemo(
    () => (
      <InputAdornment position="end" className={classes.inputAdornment}>
        <FormattedMessage id={unitLabel} />
      </InputAdornment>
    ),
    [classes.inputAdornment, unitLabel]
  );

  const additionalInputProps = useMemo(
    () => ({
      classes: {
        input: classes.input
      }
    }),
    [classes.input]
  );

  return (
    <NumberTextField
      value={value}
      onChange={onChange}
      variant="outlined"
      precision={decimalScale}
      min={0}
      max={maxValue}
      fullWidth={false}
      className={classes.inputField}
      endAdornment={endAdornment}
      additionalInputProps={additionalInputProps}
      {...rest}
    />
  );
};

AllocationInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  selectedAllocationPeriod: PropTypes.oneOf(Object.values(ALLOCATION_PERIOD))
    .isRequired,
  displayUnit: PropTypes.oneOf(Object.values(DISPLAY_UNIT_ENUM)),
  maxValue: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
};

export default AllocationInput;
